import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { isRTL } from "@/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumb from "@/components/Breadcrumb";

const BASE_URL = "https://olyinvestment.com";

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const rtl = isRTL(i18n.language);
  const currentLang = i18n.language;

  // SEO metadata
  const seoTitle = t("blog_page.seo_title");
  const seoDesc = t("blog_page.seo_desc");
  const canonicalUrl = `${BASE_URL}/${currentLang}/blog`;

  // Blog kategorileri
  const categories = [
    { id: "all", label: t("blog_page.categories.all") },
    { id: "investment", label: t("blog_page.categories.investment") },
    { id: "legal", label: t("blog_page.categories.legal") },
    { id: "lifestyle", label: t("blog_page.categories.lifestyle") },
    { id: "market", label: t("blog_page.categories.market") }
  ];

  return (
    <div className="min-h-screen bg-white" dir={rtl ? "rtl" : "ltr"}>
      <Helmet>
        <html lang={currentLang} dir={rtl ? "rtl" : "ltr"} />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Structured Data - Blog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "OlyInvestment Blog",
            "description": seoDesc,
            "url": canonicalUrl,
            "publisher": {
              "@type": "Organization",
              "name": "OlyInvestment",
              "logo": {
                "@type": "ImageObject",
                "url": `${BASE_URL}/logo.png`
              }
            }
          })}
        </script>
      </Helmet>

      <Header />
      
      <PageHeader 
        title={t("blog_page.hero_title")} 
        highlight={t("blog_page.hero_highlight")}
        subtitle={t("blog_page.hero_subtitle")}
      >
        <Breadcrumb items={[{ label: t("breadcrumb.blog") }]} />
      </PageHeader>

      {/* Kategori Filtreleri */}
      <section className="py-8 px-6 md:px-12 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`px-6 py-2.5 rounded-sm text-sm font-medium transition-all duration-300 ${
                  cat.id === "all"
                    ? "bg-[#D4AF37] text-white shadow-lg shadow-[#D4AF37]/20"
                    : "bg-[#F8F9FA] text-[#0A192F] hover:bg-[#E2E8F0]"
                }`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid - Placeholder */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Placeholder mesajı */}
            <div className="col-span-full text-center py-20">
              <div className="max-w-md mx-auto">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#F8F9FA] flex items-center justify-center">
                  <Calendar className="w-10 h-10 text-[#D4AF37]" />
                </div>
                <h3 
                  className="text-2xl font-bold text-[#0A192F] mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {t("blog_page.coming_soon_title")}
                </h3>
                <p 
                  className="text-[#4A5568] text-base leading-relaxed"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {t("blog_page.coming_soon_desc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
